import React, { useCallback, useState } from 'react';
import { Drawer, Tooltip } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Stack } from 'new/elements/Stack';
import { Button } from 'new/elements/Button';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { CodeEditorModeFooter } from './components/CodeEditorModeFooter';
import { CodeEditorModeHeader } from './components/CodeEditorModeHeader';
import { CodeEditorModeWarningBanner } from './components/CodeEditorModeWarningBanner';
import { CodeEditorMetaFields } from './components/CodeEditorMetaFields';
import { SwitchModeDialogOpen } from './components/SwitchModeDialogOpen';

import classes from './CodeEditorMode.module.scss';
import { editor } from 'monaco-editor/esm/vs/editor/editor.api';
import IMarker = editor.IMarker;
import { stripMetaFields, useCodeEditorMetaFields } from './hooks/useCodeEditorMetaFields';
import { useCopyToClipboard } from './hooks/useCopyToClipboard';

interface CodeEditorModeProps {
  open?: boolean;
  isLoading?: boolean;
  config: {};
  warning?: string;
  editorHeight?: number;
  onClose?: () => void;
  onSave?: (value: any) => void;
  cancelable?: boolean;
  onSwitchUiMode?: () => void;
  /** When set, shows connector icon + display name in the modal header. */
  connectorType?: string;
}

const monacoEditorOptions = {
  minimap: { enabled: false },
  wordWrap: 'on' as const,
  tabSize: 2,
  scrollBeyondLastLine: false,
  automaticLayout: true,
  renderLineHighlight: 'line' as const,
};

export const CodeEditorMode = (props: CodeEditorModeProps) => {
  const {
    open,
    onClose,
    config,
    onSave,
    cancelable,
    isLoading,
    onSwitchUiMode,
    connectorType,
    warning,
  } = props;
  const [markers, setMarkers] = useState<IMarker[]>([]);
  const [value, setValue] = useState(JSON.stringify(stripMetaFields(config), null, 4));
  const [isSwitchModeDialogOpen, setIsSwitchModeDialogOpen] = useState(false);
  const { isCopied, copyToClipboard } = useCopyToClipboard(value);

  const {
    nameInput,
    descriptionInput,
    isJsonValid,
    metaFieldsHelperText,
    debouncedSetValue,
    handleNameChange,
    handleDescriptionChange,
    flushAndBuildSavePayload,
  } = useCodeEditorMetaFields({
    open,
    config,
    value,
    setValue,
  });

  const syntaxMessage = markers[0]?.message || 'Syntax error detected in JSON content.';

  const onClickSave = useCallback(() => {
    const toSave = flushAndBuildSavePayload();
    onSave?.(toSave);
  }, [flushAndBuildSavePayload, onSave]);

  const openSwitchModeDialog = useCallback(() => {
    setIsSwitchModeDialogOpen(true);
  }, []);

  const closeSwitchModeDialog = useCallback(() => {
    setIsSwitchModeDialogOpen(false);
  }, []);

  const handleSwitchModeConfirm = useCallback(() => {
    setIsSwitchModeDialogOpen(false);
    onSwitchUiMode?.();
  }, [onSwitchUiMode]);

  const editorViewportModifierClass = isJsonValid ? classes.editorValid : classes.editorInvalid;
  const editorViewportClassName = `${classes.editorViewport} ${editorViewportModifierClass}`;

  return (
    <Drawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      transitionDuration={0}
      ModalProps={{ BackdropProps: { className: classes.overlay } }}
      PaperProps={{ className: classes.modalSurface }}
    >
      <Stack className={classes.modalContent}>
        <CodeEditorModeHeader connectorType={connectorType} onClose={onClose} />

        <CodeEditorMetaFields
          nameInput={nameInput}
          descriptionInput={descriptionInput}
          isJsonValid={isJsonValid}
          helperText={metaFieldsHelperText}
          onNameChange={handleNameChange}
          onDescriptionChange={handleDescriptionChange}
        />

        <div className={classes.editorShell}>
          <div className={classes.editorTopBar}>
            <div className={classes.fileMeta}>
              <InsertDriveFileOutlinedIcon fontSize="small" />
              <span>config.json</span>
              {!isJsonValid ? (
                <Tooltip title={syntaxMessage}>
                  <span className={classes.syntaxBadge}>
                    <ErrorOutlineRoundedIcon fontSize="inherit" />
                    Invalid JSON
                  </span>
                </Tooltip>
              ) : null}
            </div>

            <Button
              variant="text"
              color="inherit"
              className={
                isCopied ? `${classes.copyButton} ${classes.copyButtonCopied}` : classes.copyButton
              }
              onClick={copyToClipboard}
              startIcon={
                isCopied ? (
                  <CheckCircleRoundedIcon fontSize="small" />
                ) : (
                  <ContentCopyRoundedIcon fontSize="small" />
                )
              }
            >
              {isCopied ? 'Copied!' : 'Copy'}
            </Button>
          </div>

          <div className={editorViewportClassName}>
            <LoadableMonacoEditor
              width="100%"
              height="100%"
              value={value}
              onChange={debouncedSetValue}
              language="json"
              theme="dark"
              options={monacoEditorOptions}
              onChangeMarkers={setMarkers}
            />
          </div>
        </div>

        <CodeEditorModeWarningBanner />

        {warning && <span className={classes.warningMessage}>{warning}</span>}

        <CodeEditorModeFooter
          isLoading={isLoading}
          cancelable={cancelable}
          isJsonValid={isJsonValid}
          onClose={onClose}
          onSave={onClickSave}
          onOpenSwitchModeDialog={openSwitchModeDialog}
        />
      </Stack>
      <SwitchModeDialogOpen
        open={isSwitchModeDialogOpen}
        onClose={closeSwitchModeDialog}
        onConfirm={handleSwitchModeConfirm}
      />
    </Drawer>
  );
};
